<p-canvas
  class="app-shell"
  background="canvas"
  [sidebarStartOpen]="sidebarStartOpen()"
  [sidebarEndOpen]="sidebarEndOpen()"
  (sidebarStartUpdate)="updateSidebarStart($event)"
  (sidebarEndDismiss)="closeSidebarEnd()"
>
  <a slot="title" class="app-title" routerLink="/" aria-label="AsPIRe Cockpit Home">
    AsPIRe Cockpit
  </a>

  <p-tag slot="header-start" [color]="environmentColor()" class="app-environment-indicator">
    {{ environmentService.environmentLabel() }}
  </p-tag>
  <p-popover slot="header-start" [aria]="{ 'aria-label': 'Environment information' }">
    <p-text size="small">
      This environment indicates which data the AsPIRe Cockpit processes in the target system.
    </p-text>
  </p-popover>

  <p-button
    slot="header-end"
    type="button"
    icon="search"
    variant="secondary"
    [compact]="true"
    [hideLabel]="true"
    [aria]="{ 'aria-haspopup': 'dialog' }"
    (click)="openSearch()"
  >
    Search
  </p-button>

  <p-button
    slot="header-end"
    type="button"
    icon="user"
    variant="secondary"
    [compact]="true"
    [hideLabel]="true"
    (click)="openSidebarEnd()"
  >
    Open profile
  </p-button>

  <main class="app-main" aria-label="Application content">
    <router-outlet />
  </main>

  <div slot="sidebar-start" class="app-sidebar-start">
    <p-wordmark size="small" />
    <nav class="app-sidebar-nav" aria-label="Main navigation">
      @if (access.canAccess('OPERATIONS')) {
        <p-heading size="small">Operations</p-heading>
        <p-link-pure icon="none" class="app-sidebar-link">
          <a
            routerLink="/health"
            routerLinkActive="app-sidebar-link--active"
            ariaCurrentWhenActive="page"
          >
            <span class="app-sidebar-link__label">Health</span>
          </a>
        </p-link-pure>
      }

      @if (access.canAccess('SASI')) {
        <p-heading size="small">SASI Admin</p-heading>
        <p-link-pure icon="none" class="app-sidebar-link">
          <a
            routerLink="/manage-configurations"
            routerLinkActive="app-sidebar-link--active"
            ariaCurrentWhenActive="page"
          >
            <span class="app-sidebar-link__label">Manage Configurations</span>
          </a>
        </p-link-pure>
        <p-link-pure icon="none" class="app-sidebar-link">
          <a
            routerLink="/display-pvms"
            routerLinkActive="app-sidebar-link--active"
            ariaCurrentWhenActive="page"
          >
            <span class="app-sidebar-link__label">Display New Car Order</span>
          </a>
        </p-link-pure>
      }

      @if (access.canAccess('STOCKLOCATOR')) {
        <p-heading size="small">Stocklocator</p-heading>
        <p-link-pure icon="none" class="app-sidebar-link">
          <a
            routerLink="/stocklocator/stocks"
            routerLinkActive="app-sidebar-link--active"
            ariaCurrentWhenActive="page"
          >
            <span class="app-sidebar-link__label">Stocks</span>
          </a>
        </p-link-pure>
        <p-link-pure icon="none" class="app-sidebar-link">
          <a
            routerLink="/stocklocator/bins"
            routerLinkActive="app-sidebar-link--active"
            ariaCurrentWhenActive="page"
          >
            <span class="app-sidebar-link__label">BINs</span>
          </a>
        </p-link-pure>
      }

      @if (access.isGlobalAdmin()) {
        <p-heading size="small">Administration</p-heading>
        <p-link-pure icon="none" class="app-sidebar-link">
          <a
            routerLink="/user-management"
            routerLinkActive="app-sidebar-link--active"
            ariaCurrentWhenActive="page"
          >
            <span class="app-sidebar-link__label">User Management</span>
          </a>
        </p-link-pure>
      }

      @if (access.canAccess('EXCESS_RETURN') || access.canAccess('EXCESS_RETURN_ADMIN')) {
        <p-heading size="small">GPP Excess Return</p-heading>
      }
      @if (access.canAccess('EXCESS_RETURN')) {
        <p-link-pure icon="none" class="app-sidebar-link">
          <a
            routerLink="/excess-return"
            routerLinkActive="app-sidebar-link--active"
            [routerLinkActiveOptions]="{ exact: true }"
            ariaCurrentWhenActive="page"
          >
            <span class="app-sidebar-link__label">Inventory Assessment</span>
          </a>
        </p-link-pure>
      }
      @if (access.canAccess('EXCESS_RETURN_ADMIN')) {
        <p-link-pure icon="none" class="app-sidebar-link">
          <a
            routerLink="/excess-return/manage-configurations"
            routerLinkActive="app-sidebar-link--active"
            [routerLinkActiveOptions]="{
              paths: 'subset',
              queryParams: 'ignored',
              matrixParams: 'ignored',
              fragment: 'ignored',
            }"
            ariaCurrentWhenActive="page"
          >
            <span class="app-sidebar-link__label">Manage Configurations</span>
          </a>
        </p-link-pure>
      }

      @if (access.canAccess('CENTRAL_ROUTING')) {
        <p-heading size="small">Central Routing</p-heading>
        <p-link-pure icon="none" class="app-sidebar-link">
          <a
            routerLink="/central-routing"
            routerLinkActive="app-sidebar-link--active"
            ariaCurrentWhenActive="page"
          >
            <span class="app-sidebar-link__label">Central Routing</span>
          </a>
        </p-link-pure>
      }
    </nav>

    <p-divider />
  </div>

  <p-heading slot="sidebar-end-header" tag="h2" size="small">Account</p-heading>
  <div slot="sidebar-end" class="app-sidebar-end">
    <div class="app-profile">
      <span class="app-profile__avatar" aria-hidden="true">{{ profileInitials() }}</span>
      <p-text>
        {{ profileName() }}
        <br />
        <span class="app-profile__role">{{ profileRole() }}</span>
      </p-text>
    </div>

    <p-divider />
    <p-link-pure icon="user" class="app-sidebar-link">
      <a
        routerLink="/account"
        routerLinkActive="app-sidebar-link--active"
        ariaCurrentWhenActive="page"
      >
        <span class="app-sidebar-link__label">Account</span>
      </a>
    </p-link-pure>
    <p-link-pure icon="configurate" class="app-sidebar-link">
      <a
        routerLink="/settings"
        routerLinkActive="app-sidebar-link--active"
        ariaCurrentWhenActive="page"
      >
        <span class="app-sidebar-link__label">Settings</span>
      </a>
    </p-link-pure>
    <p-link-pure icon="information" class="app-sidebar-link">
      <a
        routerLink="/help"
        routerLinkActive="app-sidebar-link--active"
        ariaCurrentWhenActive="page"
      >
        <span class="app-sidebar-link__label">Help</span>
      </a>
    </p-link-pure>

    <p-divider />
    <p-button class="app-logout" type="button" icon="none" (click)="logout()">Logout</p-button>

    <p-divider />
    <p-text size="small" color="contrast-medium" class="app-version">
      Version: {{ environmentService.version() }}
    </p-text>
  </div>

  <footer slot="footer" class="app-footer">
    <p-text size="small" color="contrast-medium">AsPIRe Cockpit</p-text>
  </footer>
</p-canvas>

<app-session-status-banner />

<p-modal
  class="app-search-modal"
  [open]="searchOpen()"
  [aria]="{ 'aria-label': 'Search AsPIRe Cockpit' }"
  (dismiss)="closeSearch()"
>
  <p-heading slot="header" size="medium" tag="h2">Search</p-heading>
  <p-input-search
    name="app-search"
    label="Search AsPIRe Cockpit"
    placeholder="Search"
    [hideLabel]="true"
    [indicator]="true"
    [clear]="true"
    [value]="searchTerm()"
    (input)="onSearchTermInput($event)"
    (keydown)="onSearchKeydown($event)"
  />
  <div>
    @if (searchResults().length > 0) {
      @for (result of searchResults(); track result.route) {
        <button type="button" class="app-search-result" (click)="navigateToSearchResult(result)">
          <strong>{{ result.label }}</strong>
          <br />
          <span>{{ result.description }} · {{ result.route }}</span>
        </button>
      }
    } @else {
      <p-text color="contrast-medium">No results found.</p-text>
    }
  </div>
</p-modal>
